import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  IconUsers,
  IconServer,
  IconSearch,
  IconRefresh,
  IconWorld,
  IconPlus,
  IconShield
} from '@tabler/icons-react';
import { useNodes } from '../hooks/useNodes';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { useAdminTunnels } from '../hooks/useAdminTunnels';
import NodeStatsCard from '../components/admin/nodes/NodeStatsCard';
import NodesGrid from '../components/admin/nodes/NodesGrid';
import EmptyState from '../components/admin/nodes/EmptyState';
import CreateNodeModal from '../components/admin/nodes/CreateNodeModal';
import EditNodeModal from '../components/admin/nodes/EditNodeModal';
import UsersTable from '../components/admin/UsersTable';
import TunnelsTable from '../components/admin/TunnelsTable';
import AdminModal from '../components/admin/AdminModal';

const AdminPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState(0);
  const [tunnelLimit, setTunnelLimit] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [planDuration, setPlanDuration] = useState(1);

  const {
    nodes,
    loading: nodesLoading,
    fetchNodes,
    createNode,
    updateNode,
    deleteNode
  } = useNodes();

  const {
    users,
    fetchUsers,
    banUser,
    unbanUser,
    setTunnelLimit: setUserTunnelLimit,
    setPlan,
    toggleAdmin
  } = useAdminUsers();

  const {
    tunnels,
    fetchTunnels,
    blockTunnel,
    unblockTunnel,
    deleteTunnel
  } = useAdminTunnels();

   const [showCreateNodeModal, setShowCreateNodeModal] = useState(false);
  const [showEditNodeModal, setShowEditNodeModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    } else if (!authLoading && user && !user.is_admin) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchUsers();
      fetchTunnels();
      fetchNodes();
      const interval = setInterval(fetchNodes, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUsers, fetchTunnels, fetchNodes]);

  const openModal = (type, item) => {
    setModalType(type);
    setSelectedItem(item);
    setBanReason('');
    setBanDuration(0);
    setTunnelLimit(item?.tunnel_limit || 0);
    setSelectedPlan(item?.plan || 'free');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleModalConfirm = async () => {
    if (modalType === 'banUser') {
      const result = await banUser(selectedItem.id, banReason, banDuration);
      if (result.success) {
        closeModal();
      }
    } else if (modalType === 'limit') {
      const result = await setUserTunnelLimit(selectedItem.id, tunnelLimit);
      if (result.success) {
        closeModal();
      }
    } else if (modalType === 'plan') {
      const result = await setPlan(selectedItem.id, selectedPlan, planDuration);
      if (result.success) {
        closeModal();
      }
    } else if (modalType === 'blockTunnel') {
      const result = await blockTunnel(selectedItem.id, banReason, banDuration);
      if (result.success) {
        closeModal();
      }
    }
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTunnels = tunnels.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openEditNodeModal = (node) => {
    setSelectedNode(node);
    setShowEditNodeModal(true);
  };

  const handleDeleteNode = async (nodeId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту ноду?')) return;
    const result = await deleteNode(nodeId);
    if (!result.success) {
      alert(result.error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-dark-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-dark-950 pt-20 pb-12 transition-colors duration-300">
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/css/flag-icons.min.css" />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <IconShield className="w-8 h-8 text-primary-500" />
              Админ-панель
            </h1>
            <p className="text-gray-600 dark:text-dark-600 mt-1">
              Управление пользователями и туннелями
            </p>
          </div>
          <button
            onClick={fetchNodes}
            className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
          >
            <IconRefresh className="w-4 h-4" />
            Обновить
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-primary-500 text-black'
                : 'bg-white dark:bg-dark-900 text-gray-700 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-800'
            }`}
          >
            <IconUsers className="w-5 h-5" />
            Пользователи ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('tunnels')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'tunnels'
                ? 'bg-primary-500 text-black'
                : 'bg-white dark:bg-dark-900 text-gray-700 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-800'
            }`}
          >
            <IconServer className="w-5 h-5" />
            Туннели ({tunnels.length})
          </button>
          <button
            onClick={() => setActiveTab('nodes')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'nodes'
                ? 'bg-primary-500 text-black'
                : 'bg-white dark:bg-dark-900 text-gray-700 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-800'
            }`}
          >
            <IconWorld className="w-5 h-5" />
            Ноды ({nodes.length})
          </button>
        </div>

        {/* IconSearch */}
        <div className="relative mb-6">
          <IconSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск..."
            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-dark-900 border border-gray-300 dark:border-dark-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* IconUsers Table */}
        {activeTab === 'users' && (
          <UsersTable
            users={filteredUsers}
            onEditPlan={(u) => openModal('plan', u)}
            onEditLimit={(u) => openModal('limit', u)}
            onToggleAdmin={(userId, level) => toggleAdmin(userId, level)}
            onBan={(u) => openModal('banUser', u)}
            onUnban={(userId) => unbanUser(userId)}
          />
        )}

        {/* Tunnels Table */}
        {activeTab === 'tunnels' && (
          <TunnelsTable
            tunnels={filteredTunnels}
            onBlock={(t) => openModal('blockTunnel', t)}
            onUnblock={(tunnelId) => unblockTunnel(tunnelId)}
            onDelete={(tunnelId) => deleteTunnel(tunnelId)}
          />
        )}

        {/* Nodes Grid */}
        {activeTab === 'nodes' && (
          <>
            <NodeStatsCard nodes={nodes} />
            
            {nodes.length === 0 ? (
              <EmptyState onCreate={() => setShowCreateNodeModal(true)} />
            ) : (
              <NodesGrid 
                nodes={nodes}
                onEdit={openEditNodeModal}
                onDelete={handleDeleteNode}
              />
            )}
          </>
        )}

        <button
          onClick={() => setShowCreateNodeModal(true)}
          className={`flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium text-black transition-colors ${activeTab !== 'nodes' ? 'hidden' : 'fixed bottom-8 right-8'}`}
        >
          <IconPlus className="w-5 h-5" />
          Добавить ноду
        </button>

      </div>
      <AdminModal
        isOpen={modalOpen}
        modalType={modalType}
        item={selectedItem}
        onClose={closeModal}
        onConfirm={handleModalConfirm}
        planDuration={planDuration}
        setPlanDuration={setPlanDuration}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        tunnelLimit={tunnelLimit}
        setTunnelLimit={setTunnelLimit}
        banReason={banReason}
        setBanReason={setBanReason}
        banDuration={banDuration}
        setBanDuration={setBanDuration}
      />

      <CreateNodeModal
        isOpen={showCreateNodeModal}
        onClose={() => setShowCreateNodeModal(false)}
        onCreate={async (data) => {
          const result = await createNode(data);
          if (result.success) {
            await fetchNodes();
          } else {
            alert(result.error);
          }
        }}
      />

      <EditNodeModal
        isOpen={showEditNodeModal}
        node={selectedNode}
        onClose={() => {
          setShowEditNodeModal(false);
          setSelectedNode(null);
        }}
        onUpdate={async (nodeId, data) => {
          const result = await updateNode(nodeId, data);
          if (result.success) {
            await fetchNodes();
          } else {
            alert(result.error);
          }
        }}
      />
    </div>
  );
};

export default AdminPage;
